import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import './css/index.scss';
import { useDispatch, useSelector } from 'react-redux';
import LocalStorageServices from '../../../../helpers/LocalStorageServices';
import MyToggle from '../../../../components/MyToggle';
import MyButton from '../../../../components/MyButton';
import SearchInput from '../../../../components/SearchInput';
import SelectButton from '../../../../components/SelectButton';
import Api from '../../../../Api';
import sendRequest from '../../../../helpers/sendRequest';
import { getAdminCategoriesRequest, getAdminSubCategoriesRequest } from '../../../../store/actions/categories';
import { getCountriesRequest } from '../../../../store/actions/staticData';
import MyModal from '../../../../components/MyModal';
import useComponentVisible from '../../../../helpers/hooks/useComponentVisible';

const DEFAULT_PRICING = {
  position_1: 30,
  position_2: 18,
  position_3: 10,
  discount_1m: 0,
  discount_3m: 10,
  discount_6m: 15,
};

const PRICE_MIN = 0.6;
const PRICE_MAX = 999999;
const PERCENT_MIN = 0;
const PERCENT_MAX = 100;

function formatPositions(p) {
  if (!p) return '—';
  const parts = [];
  if (p.position_1 != null) parts.push(`1: ${p.position_1}$`);
  if (p.position_2 != null) parts.push(`2: ${p.position_2}$`);
  if (p.position_3 != null) parts.push(`3: ${p.position_3}$`);
  return parts.length ? parts.join('                     ') : '—';
}

function formatDiscounts(p) {
  if (!p) return '—';
  const parts = [];
  if (p.discount_1m != null) parts.push(`1 мес.: ${p.discount_1m}%`);
  if (p.discount_3m != null) parts.push(`3 мес.: ${p.discount_3m}%`);
  if (p.discount_6m != null) parts.push(`6 мес.: ${p.discount_6m}%`);
  return parts.length ? parts.join('           ') : '—';
}

function formatPricing(p) {
  if (!p) return '—';
  return [formatPositions(p), formatDiscounts(p)].filter(Boolean).join('\n\n\n');
}

function SlotRow({ slot, onDelete }) {
  const cityName = slot.city_id?.name ?? '—';
  const categoryName = slot.category_id?.name ?? '—';
  const subName = slot.sub_category_id?.name ?? '—';
  const p = slot.organization_promotion_pricing;

  return (
    <tr>
      <td className="org_promotion_table_cell">{cityName}</td>
      <td className="org_promotion_table_cell">{categoryName}</td>
      <td className="org_promotion_table_cell">{subName}</td>
      <td className="org_promotion_table_cell org_promotion_cell_places"><pre>{formatPositions(p)}</pre></td>
      <td className="org_promotion_table_cell org_promotion_cell_periods"><pre>{formatDiscounts(p)}</pre></td>
      <td className="org_promotion_table_cell org_promotion_cell_actions">
        <MyButton text="Удалить" secondary onClick={() => onDelete(slot)} className="org_promotion_delete_btn" />
      </td>
    </tr>
  );
}

function getInputValue(e) {
  return e?.target != null ? e.target.value : (typeof e === 'string' || typeof e === 'number' ? String(e) : '');
}

function PricingForm({
  initial, onSubmit, onCancel, saving,
}) {
  const [position_1, setPosition_1] = useState(initial?.position_1 ?? DEFAULT_PRICING.position_1);
  const [position_2, setPosition_2] = useState(initial?.position_2 ?? DEFAULT_PRICING.position_2);
  const [position_3, setPosition_3] = useState(initial?.position_3 ?? DEFAULT_PRICING.position_3);
  const [discount_1m, setDiscount_1m] = useState(initial?.discount_1m ?? DEFAULT_PRICING.discount_1m);
  const [discount_3m, setDiscount_3m] = useState(initial?.discount_3m ?? DEFAULT_PRICING.discount_3m);
  const [discount_6m, setDiscount_6m] = useState(initial?.discount_6m ?? DEFAULT_PRICING.discount_6m);
  const [error, setError] = useState('');

  const validate = () => {
    const prices = [
      { v: position_1, name: 'Позиция 1' },
      { v: position_2, name: 'Позиция 2' },
      { v: position_3, name: 'Позиция 3' },
    ];
    for (const { v, name } of prices) {
      const num = Number(String(v).replace(/,/, '.'));
      if (Number.isNaN(num) || num < PRICE_MIN || num > PRICE_MAX) {
        setError(`${name}: от ${PRICE_MIN} до ${PRICE_MAX}, формат X.XX`);
        return false;
      }
    }
    const percents = [
      { v: discount_1m, name: 'Скидка 1 мес' },
      { v: discount_3m, name: 'Скидка 3 мес' },
      { v: discount_6m, name: 'Скидка 6 мес' },
    ];
    for (const { v, name } of percents) {
      const num = Number(String(v).replace(/,/, '.'));
      if (Number.isNaN(num) || num < PERCENT_MIN || num > PERCENT_MAX) {
        setError(`${name}: от ${PERCENT_MIN} до ${PERCENT_MAX}%`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const rounded = (v) => Math.round(Number(String(v).replace(/,/, '.')) * 100) / 100;
    onSubmit({
      organization_promotion_pricing: {
        position_1: rounded(position_1),
        position_2: rounded(position_2),
        position_3: rounded(position_3),
        discount_1m: Number(String(discount_1m).replace(/,/, '.')),
        discount_3m: Number(String(discount_3m).replace(/,/, '.')),
        discount_6m: Number(String(discount_6m).replace(/,/, '.')),
      },
    });
  };

  return (
    <div className="org_promotion_pricing_form">
      <SearchInput wrapperClassName="org_promotion_input" labelText="Позиция 1 ($/мес)" value={String(position_1)} onChange={(e) => setPosition_1(getInputValue(e))} withLeftIcon={false} withCloseIcon={false} />
      <SearchInput wrapperClassName="org_promotion_input" labelText="Позиция 2 ($/мес)" value={String(position_2)} onChange={(e) => setPosition_2(getInputValue(e))} withLeftIcon={false} withCloseIcon={false} />
      <SearchInput wrapperClassName="org_promotion_input" labelText="Позиция 3 ($/мес)" value={String(position_3)} onChange={(e) => setPosition_3(getInputValue(e))} withLeftIcon={false} withCloseIcon={false} />
      <SearchInput wrapperClassName="org_promotion_input" labelText="Скидка 1 мес (%)" value={String(discount_1m)} onChange={(e) => setDiscount_1m(getInputValue(e))} withLeftIcon={false} withCloseIcon={false} />
      <SearchInput wrapperClassName="org_promotion_input" labelText="Скидка 3 мес (%)" value={String(discount_3m)} onChange={(e) => setDiscount_3m(getInputValue(e))} withLeftIcon={false} withCloseIcon={false} />
      <SearchInput wrapperClassName="org_promotion_input" labelText="Скидка 6 мес (%)" value={String(discount_6m)} onChange={(e) => setDiscount_6m(getInputValue(e))} withLeftIcon={false} withCloseIcon={false} />
      {error && <p className="org_promotion_form_error">{error}</p>}
      <div className="org_promotion_form_buttons">
        <MyButton text="Сохранить" onClick={handleSubmit} loading={saving} />
        <MyButton text="Отмена" secondary onClick={onCancel} />
      </div>
    </div>
  );
}

const DROPDOWN_STATES = {
  category: 'category', subcategory: 'subcategory', country: 'country', city: 'city',
};

function OrganizationPromotionSettings({ data, loading, onUpdate }) {
  const dispatch = useDispatch();
  const { categories, subCategories } = useSelector((store) => store.categories);
  const countries = useSelector((store) => store.staticData?.countries ?? []);

  const [workingWithOnline, setWorkingWithOnline] = useState(Boolean(LocalStorageServices.getIsWorkingWithOnlineCategories()));
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCountryForCity, setSelectedCountryForCity] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormPricing, setAddFormPricing] = useState({ ...DEFAULT_PRICING });
  const [saving, setSaving] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dropdownState, setDropdownState] = useState(null);
  const { ref: dropdownsWrapRef, isComponentVisible: dropdownOpen, setIsComponentVisible: setDropdownOpen } = useComponentVisible(false);

  const defaultSetting = data?.default ?? null;
  const bySlot = data?.by_slot ?? [];

  const onlineCityFromSlots = bySlot.find((s) => s.city_id?.name === 'Онлайн')?.city_id;
  const storedOnlineCity = LocalStorageServices.getPaymentOnlineCity();
  const effectiveOnlineCity = onlineCityFromSlots || storedOnlineCity;

  useEffect(() => {
    if (onlineCityFromSlots?._id) LocalStorageServices.setPaymentOnlineCity(onlineCityFromSlots);
  }, [onlineCityFromSlots?._id]);

  const loadCategories = useCallback(() => {
    dispatch(getAdminCategoriesRequest({ is_online: workingWithOnline, with_sub_categories: 'true' }));
  }, [dispatch, workingWithOnline]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const filteredCategories = Array.isArray(categories)
    ? categories.filter((c) => Boolean(c.is_online) === workingWithOnline)
    : [];
  const currentSubCategories = selectedCategory ? subCategories : [];
  const sortedCountries = useMemo(() => [...countries].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru')), [countries]);

  useEffect(() => {
    if (selectedCategory?._id) dispatch(getAdminSubCategoriesRequest({ category_id: selectedCategory._id }));
  }, [selectedCategory?._id, dispatch]);

  const loadCountries = useCallback(() => {
    dispatch(getCountriesRequest());
  }, [dispatch]);

  const loadCities = useCallback(async (countryId) => {
    const res = await sendRequest({ request: Api.getCities, payload: { country_id: countryId }, warnErrorText: 'cities' });
    if (res?.status === 'ok') setCities(res.cities || []);
  }, []);

  useEffect(() => {
    if (showAddForm && !workingWithOnline && countries.length === 0) loadCountries();
  }, [showAddForm, workingWithOnline, countries.length, loadCountries]);

  useEffect(() => {
    if (selectedCountryForCity?._id) loadCities(selectedCountryForCity._id);
  }, [selectedCountryForCity?._id, loadCities]);

  const openDropdown = (state) => {
    setDropdownState(state);
    setDropdownOpen(true);
  };

  const handleDeleteSlot = (slot) => {
    setSlotToDelete(slot);
    setDeleteConfirmVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!slotToDelete?._id) return;
    const res = await sendRequest({
      request: Api.deletePaymentSetting,
      payload: slotToDelete._id,
      warnErrorText: 'delete payment setting',
      setLoading: setDeleting,
    });
    if (res?.status === 'ok' && res?.deleted) {
      onUpdate();
      setDeleteConfirmVisible(false);
      setSlotToDelete(null);
      setModalText('Настройка удалена. Для данной связки будет применено дефолтное значение.');
      setModalVisible(true);
    } else {
      setModalText(res?.message || 'Ошибка удаления');
      setModalVisible(true);
    }
  };

  const validateAddForm = () => {
    const p = addFormPricing;
    const prices = [
      { v: p.position_1, name: 'Позиция 1' },
      { v: p.position_2, name: 'Позиция 2' },
      { v: p.position_3, name: 'Позиция 3' },
    ];
    for (const { v, name } of prices) {
      const num = Number(String(v).replace(/,/, '.'));
      if (Number.isNaN(num) || num < PRICE_MIN || num > PRICE_MAX) {
        return `${name}: от ${PRICE_MIN} до ${PRICE_MAX}, формат X.XX`;
      }
    }
    const percents = [
      { v: p.discount_1m, name: 'Скидка 1 мес' },
      { v: p.discount_3m, name: 'Скидка 3 мес' },
      { v: p.discount_6m, name: 'Скидка 6 мес' },
    ];
    for (const { v, name } of percents) {
      const num = Number(String(v).replace(/,/, '.'));
      if (Number.isNaN(num) || num < PERCENT_MIN || num > PERCENT_MAX) {
        return `${name}: от ${PERCENT_MIN} до ${PERCENT_MAX}%`;
      }
    }
    return null;
  };

  const handleAddSlot = async () => {
    let cityId = workingWithOnline ? effectiveOnlineCity?._id : selectedCity?._id;
    if (workingWithOnline && !cityId) {
      const res = await sendRequest({ request: Api.getOnlineCity, warnErrorText: 'online city' });
      const city = res?.city;
      if (city?._id) {
        LocalStorageServices.setPaymentOnlineCity(city);
        cityId = city._id;
      }
    }
    if (!cityId) {
      setModalText(workingWithOnline ? 'Город «Онлайн» не найден. Обратитесь к разработчику для настройки эндпоинта /api/static-data/online-city.' : 'Выберите страну и город.');
      setModalVisible(true);
      return;
    }
    if (!selectedCategory?._id) {
      setModalText('Выберите категорию.');
      setModalVisible(true);
      return;
    }
    const hasSubs = currentSubCategories.length > 0;
    if (hasSubs && !selectedSubCategory?._id) {
      setModalText('Выберите подкатегорию.');
      setModalVisible(true);
      return;
    }
    const err = validateAddForm();
    if (err) {
      setModalText(err);
      setModalVisible(true);
      return;
    }

    const rounded = (v) => Math.round(Number(String(v).replace(/,/, '.')) * 100) / 100;
    const body = {
      action_to_pay_for: 'organization_promotion',
      city_id: cityId,
      category_id: selectedCategory._id,
      sub_category_id: hasSubs ? selectedSubCategory._id : '',
      value: rounded(addFormPricing.position_1) || 30,
      currency: 'usd',
      organization_promotion_pricing: {
        position_1: rounded(addFormPricing.position_1) || DEFAULT_PRICING.position_1,
        position_2: rounded(addFormPricing.position_2) ?? DEFAULT_PRICING.position_2,
        position_3: rounded(addFormPricing.position_3) ?? DEFAULT_PRICING.position_3,
        discount_1m: Number(String(addFormPricing.discount_1m).replace(/,/, '.')) ?? DEFAULT_PRICING.discount_1m,
        discount_3m: Number(String(addFormPricing.discount_3m).replace(/,/, '.')) ?? DEFAULT_PRICING.discount_3m,
        discount_6m: Number(String(addFormPricing.discount_6m).replace(/,/, '.')) ?? DEFAULT_PRICING.discount_6m,
      },
    };
    const res = await sendRequest({
      request: Api.createOrUpdatePaymentSettingSlot,
      payload: body,
      warnErrorText: 'create/update slot',
      setLoading: setSaving,
    });
    if (res?.status === 'ok') {
      onUpdate();
      setShowAddForm(false);
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setSelectedCity(null);
      setSelectedCountryForCity(null);
      setCities([]);
      setModalText(res.updated ? 'Настройка обновлена.' : 'Настройка добавлена.');
      setModalVisible(true);
    } else {
      setModalText(res?.message || 'Ошибка');
      setModalVisible(true);
    }
  };

  const openAddForm = () => {
    setShowAddForm(true);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedCity(null);
    setSelectedCountryForCity(null);
    setAddFormPricing({ ...DEFAULT_PRICING });
    setCities([]);
    setDropdownState(null);
    setDropdownOpen(false);
  };

  const addFormReady = useMemo(() => {
    if (!selectedCategory) return false;
    if (currentSubCategories.length > 0 && !selectedSubCategory) return false;
    if (!workingWithOnline && (!selectedCountryForCity || !selectedCity)) return false;
    return true;
  }, [selectedCategory, currentSubCategories.length, selectedSubCategory, workingWithOnline, selectedCountryForCity, selectedCity]);

  if (loading && !data) {
    return <p className="org_promotion_loading">Загрузка...</p>;
  }

  return (
    <div className="organization_promotion_settings">
      <h3 className="org_promotion_title">Продвижение организации</h3>

      <p className="org_promotion_default_intro">
        Для всех связок город + категория/подкатегория действует дефолтная настройка:
      </p>
      {defaultSetting ? (
        <div className="org_promotion_default_block">
          <pre className="org_promotion_pricing_pre">{formatPricing(defaultSetting.organization_promotion_pricing)}</pre>
          <p className="org_promotion_default_contact">Если хотите поменять дефолтные настройки, то обратитесь к разработчику.</p>
        </div>
      ) : (
        <p className="org_promotion_no_default">Дефолтная настройка не задана.</p>
      )}

      <h4 className="org_promotion_subtitle">Связки с отдельными настройками</h4>
      {bySlot.length > 0 ? (
        <table className="org_promotion_table">
          <thead>
            <tr>
              <th className="org_promotion_table_cell">Город</th>
              <th className="org_promotion_table_cell">Категория</th>
              <th className="org_promotion_table_cell">Подкатегория</th>
              <th className="org_promotion_table_cell org_promotion_cell_places">Места (цена $)</th>
              <th className="org_promotion_table_cell org_promotion_cell_periods">Периоды (скидка %)</th>
              <th className="org_promotion_table_cell org_promotion_cell_actions" />
            </tr>
          </thead>
          <tbody>
            {bySlot.map((slot) => (
              <SlotRow key={slot._id} slot={slot} onDelete={handleDeleteSlot} />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="org_promotion_empty_slots">Нет отдельных настроек по слотам.</p>
      )}

      <h4 className="org_promotion_subtitle">Добавить настройку для связки</h4>
      <div className="org_promotion_toggle_row">
        <span>Онлайн категории</span>
        <MyToggle value={workingWithOnline} onClick={() => { const v = !workingWithOnline; setWorkingWithOnline(v); LocalStorageServices.setIsWorkingWithOnlineCategories(v); setSelectedCategory(null); setSelectedSubCategory(null); loadCategories(); }} />
      </div>

      {!showAddForm ? (
        <MyButton text="Добавить связку" onClick={openAddForm} className="org_promotion_add_btn" />
      ) : (
        <div className="org_promotion_add_section">
          {workingWithOnline && (
            <p className="org_promotion_online_notice">Применится к ОНЛАЙН городу.</p>
          )}

          <div className="org_promotion_dropdowns_row" ref={dropdownsWrapRef}>
            <div className="org_promotion_select_wrapper">
              <SelectButton
                label="Категория"
                value={selectedCategory?.name ?? ''}
                setTempComponentState={(s) => { setDropdownState(s ? DROPDOWN_STATES.category : null); setDropdownOpen(!!s); }}
                componentStateToSet={DROPDOWN_STATES.category}
                isActive={dropdownOpen && dropdownState === DROPDOWN_STATES.category}
              />
              {dropdownOpen && dropdownState === DROPDOWN_STATES.category && (
                <div className="org_promotion_dropdown_list">
                  {filteredCategories.map((cat) => (
                    <div key={cat._id} className="org_promotion_dropdown_item" onClick={() => { setSelectedCategory(cat); setSelectedSubCategory(null); setDropdownOpen(false); }}>
                      {cat.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedCategory && currentSubCategories.length > 0 && (
              <div className="org_promotion_select_wrapper">
                <SelectButton
                  label="Подкатегория"
                  value={selectedSubCategory?.name ?? ''}
                  setTempComponentState={(s) => { setDropdownState(s ? DROPDOWN_STATES.subcategory : null); setDropdownOpen(!!s); }}
                  componentStateToSet={DROPDOWN_STATES.subcategory}
                  isActive={dropdownOpen && dropdownState === DROPDOWN_STATES.subcategory}
                />
                {dropdownOpen && dropdownState === DROPDOWN_STATES.subcategory && (
                  <div className="org_promotion_dropdown_list">
                    {currentSubCategories.map((sub) => (
                      <div key={sub._id} className="org_promotion_dropdown_item" onClick={() => { setSelectedSubCategory(sub); setDropdownOpen(false); }}>
                        {sub.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!workingWithOnline && (
              <>
                <div className="org_promotion_select_wrapper">
                  <SelectButton
                    label="Страна"
                    value={selectedCountryForCity?.name ?? ''}
                    setTempComponentState={(s) => { setDropdownState(s ? DROPDOWN_STATES.country : null); setDropdownOpen(!!s); }}
                    componentStateToSet={DROPDOWN_STATES.country}
                    isActive={dropdownOpen && dropdownState === DROPDOWN_STATES.country}
                  />
                  {dropdownOpen && dropdownState === DROPDOWN_STATES.country && (
                    <div className="org_promotion_dropdown_list">
                      {sortedCountries.map((c) => (
                        <div key={c._id} className="org_promotion_dropdown_item" onClick={() => { setSelectedCountryForCity(c); setSelectedCity(null); setDropdownOpen(false); }}>
                          {c.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedCountryForCity && (
                  <div className="org_promotion_select_wrapper">
                    <SelectButton
                      label="Город"
                      value={selectedCity?.name ?? ''}
                      setTempComponentState={(s) => { setDropdownState(s ? DROPDOWN_STATES.city : null); setDropdownOpen(!!s); }}
                      componentStateToSet={DROPDOWN_STATES.city}
                      isActive={dropdownOpen && dropdownState === DROPDOWN_STATES.city}
                    />
                    {dropdownOpen && dropdownState === DROPDOWN_STATES.city && (
                      <div className="org_promotion_dropdown_list">
                        {cities.map((city) => (
                          <div key={city._id} className="org_promotion_dropdown_item" onClick={() => { setSelectedCity(city); setDropdownOpen(false); }}>
                            {city.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <p className="org_promotion_pricing_hint">
            Цены: от
            {' '}
            {PRICE_MIN}
            {' '}
            до
            {PRICE_MAX}
            {' '}
            (формат X.XX). Скидки: от
            {' '}
            {PERCENT_MIN}
            {' '}
            до
            {PERCENT_MAX}
            %.
          </p>
          <div className="org_promotion_add_pricing">
            <div className="org_promotion_pricing_group org_promotion_pricing_group_sum">
              <p className="org_promotion_pricing_group_title">Сумма ($)</p>
              <SearchInput wrapperClassName="org_promotion_input org_promotion_input_spaced" labelText="Позиция 1 ($)" value={String(addFormPricing.position_1)} onChange={(e) => setAddFormPricing((p) => ({ ...p, position_1: getInputValue(e) }))} withLeftIcon={false} withCloseIcon={false} disabled={!addFormReady} />
              <SearchInput wrapperClassName="org_promotion_input org_promotion_input_spaced" labelText="Позиция 2 ($)" value={String(addFormPricing.position_2)} onChange={(e) => setAddFormPricing((p) => ({ ...p, position_2: getInputValue(e) }))} withLeftIcon={false} withCloseIcon={false} disabled={!addFormReady} />
              <SearchInput wrapperClassName="org_promotion_input org_promotion_input_spaced" labelText="Позиция 3 ($)" value={String(addFormPricing.position_3)} onChange={(e) => setAddFormPricing((p) => ({ ...p, position_3: getInputValue(e) }))} withLeftIcon={false} withCloseIcon={false} disabled={!addFormReady} />
            </div>
            <div className="org_promotion_pricing_group org_promotion_pricing_group_percent">
              <p className="org_promotion_pricing_group_title">Скидки (%)</p>
              <SearchInput wrapperClassName="org_promotion_input org_promotion_input_spaced" labelText="Скидка 1м (%)" value={String(addFormPricing.discount_1m)} onChange={(e) => setAddFormPricing((p) => ({ ...p, discount_1m: getInputValue(e) }))} withLeftIcon={false} withCloseIcon={false} disabled={!addFormReady} />
              <SearchInput wrapperClassName="org_promotion_input org_promotion_input_spaced" labelText="Скидка 3м (%)" value={String(addFormPricing.discount_3m)} onChange={(e) => setAddFormPricing((p) => ({ ...p, discount_3m: getInputValue(e) }))} withLeftIcon={false} withCloseIcon={false} disabled={!addFormReady} />
              <SearchInput wrapperClassName="org_promotion_input org_promotion_input_spaced" labelText="Скидка 6м (%)" value={String(addFormPricing.discount_6m)} onChange={(e) => setAddFormPricing((p) => ({ ...p, discount_6m: getInputValue(e) }))} withLeftIcon={false} withCloseIcon={false} disabled={!addFormReady} />
            </div>
          </div>
          <div className="org_promotion_form_buttons">
            <MyButton text="Сохранить" onClick={handleAddSlot} loading={saving} disabled={!addFormReady} />
            <MyButton text="Отмена" secondary onClick={() => setShowAddForm(false)} />
          </div>
        </div>
      )}

      <MyModal isOpen={deleteConfirmVisible} closeFunction={() => { setDeleteConfirmVisible(false); setSlotToDelete(null); }}>
        <div style={{ padding: '20px' }}>
          <p className="org_promotion_modal_title">Удалить настройку для связки?</p>
          <p style={{ marginBottom: '20px', lineHeight: 1.5 }}>
            Для данной связки в дальнейшем будет применено дефолтное значение.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <MyButton text="Удалить" onClick={handleConfirmDelete} loading={deleting} />
            <MyButton text="Отмена" secondary onClick={() => { setDeleteConfirmVisible(false); setSlotToDelete(null); }} disabled={deleting} />
          </div>
        </div>
      </MyModal>

      <MyModal isOpen={modalVisible} closeFunction={() => setModalVisible(false)}>
        <div style={{ padding: '20px' }}>
          <p style={{ whiteSpace: 'pre-wrap' }}>{modalText}</p>
          <MyButton text="ОК" onClick={() => setModalVisible(false)} />
        </div>
      </MyModal>
    </div>
  );
}

export default OrganizationPromotionSettings;
